import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Info } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcMethodology } from "@/lib/api";

export default function BusinessCenterMethodology() {
  const [m, setM] = useState(null);
  useEffect(() => {
    document.title = "Scoring Methodology | WebsiteTech AI Business Center";
    bcMethodology().then(setM).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-methodology">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">The 100-point WebsiteTech Score methodology</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">A single, publicly documented scoring model applied to every tool in the directory. Version {m?.version || "1.0.0"} · effective {m?.effective_at}.</p>

          <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              {m?.disclosures || "Every score is documented with evidence and testing dates. Vendor claims are separated from WebsiteTech-observed facts. Commercial relationships are declared on every review."}
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {(m?.criteria || []).map((c, i) => (
              <div key={c.key} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`bc-crit-${c.key}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Criterion {i + 1}</div>
                    <div className="text-lg font-bold text-slate-900 mt-0.5">{c.label}</div>
                  </div>
                  <div className="text-3xl font-black text-indigo-600 tabular-nums">{c.points} <span className="text-sm text-slate-400 font-semibold">pts</span></div>
                </div>
                <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-slate-700">
                  {(c.criteria || []).map((k, j) => (
                    <li key={j} className="flex items-start gap-2"><span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" /> {k}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-xl bg-slate-100 border border-slate-200 p-5 text-sm text-slate-700 flex items-start gap-3">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Business Fit Score:</strong> In addition to the general 100-point Score, every review carries a personalized <em>Business Fit Score</em> derived from your industry, size, geography, languages, existing systems, budget and internal technical capacity. A highly-rated product may still receive a low Business Fit Score for a particular customer.
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
